import * as React from 'react';

import type {
  AIMessageDraft,
  AudienceDelta,
  CohortAudience,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { useCohortCreate } from 'bundles/enterprise-admin-messaging-v2/hooks/useCohortCreate';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

export type CohortFlowStatus = 'idle' | 'creating' | 'success' | 'disambiguation' | 'error';

export type CohortSendArgs = {
  orgId: string;
  cohortId: string;
  preset: string;
  callToAction: string;
  audienceDelta?: AudienceDelta;
};

type Params = {
  open: boolean;
  prompt: string;
  orgId: string;
  programIds?: string[];
  updateDraft: (partial: Partial<AIMessageDraft>) => void;
};

type Result = {
  /** Fires the NLQ cohort creation. Resolves `true` on success, `false` on any failure. */
  createCohort: () => Promise<boolean>;
  status: CohortFlowStatus;
  cohortId: string | null;
  isCreating: boolean;
  errorMessage: string | null;
  warnings: string[];
  cohortAudience: CohortAudience | undefined;
  resolveSendArgs: (ctaText: string, callToAction?: string, audienceDelta?: AudienceDelta) => CohortSendArgs | null;
  dismissError: () => void;
};

/**
 * Business-logic orchestrator for the AI cohort flow. Owns the full cohort
 * lifecycle: creation via NLQ, status handling (SUCCESS / DISAMBIGUATION /
 * ERROR), draft updates, and exposes `cohortAudience` for the learner-list
 * screens and `resolveSendArgs` for the send path.
 *
 * Extracted from `AIMessageModal` so the modal stays thin (UI wiring only)
 * and this logic is independently unit-testable.
 */
export const useAICohortFlow = ({ open, prompt, orgId, programIds, updateDraft }: Params): Result => {
  const [status, setStatus] = React.useState<CohortFlowStatus>('idle');
  const [cohortId, setCohortId] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [warnings, setWarnings] = React.useState<string[]>([]);

  const { createCohort: mutateCreateCohort, loading: isCreating } = useCohortCreate();

  // Stable ref to the latest updateDraft so the async callback never closes
  // over a stale version (the parent re-creates it on every render via useCallback).
  const updateDraftRef = React.useRef(updateDraft);
  React.useEffect(() => {
    updateDraftRef.current = updateDraft;
  }, [updateDraft]);

  // Stable ref for prompt so createCohort reads the latest at call-time.
  const promptRef = React.useRef(prompt);
  React.useEffect(() => {
    promptRef.current = prompt;
  }, [prompt]);

  // Guard against duplicate in-flight calls (e.g. rapid Generate clicks).
  const isCreatingRef = React.useRef(false);

  // Reset all internal state when the modal closes.
  React.useEffect(() => {
    if (!open) {
      setStatus('idle');
      setCohortId(null);
      setErrorMessage(null);
      setWarnings([]);
      isCreatingRef.current = false;
    }
  }, [open]);

  const createCohort = React.useCallback(async (): Promise<boolean> => {
    const currentPrompt = promptRef.current;
    if (!currentPrompt || !orgId || isCreatingRef.current) return false;

    isCreatingRef.current = true;

    setStatus('creating');
    setErrorMessage(null);
    setWarnings([]);

    const result = await mutateCreateCohort(currentPrompt, orgId, programIds);
    isCreatingRef.current = false;

    switch (result.status) {
      case 'SUCCESS': {
        if (result.learners.length === 0) {
          setStatus('error');
          setErrorMessage(
            _t(
              'No learners match this audience. Your criteria are very specific. Here are closer matches in your organization you could try instead.'
            )
          );
          return false;
        }
        setStatus('success');
        setCohortId(result.cohortId);
        setWarnings(result.warnings);
        updateDraftRef.current({
          cohortId: result.cohortId,
          resolvedProgramIds: result.resolvedProgramIds,
          learners: result.learners,
          originalLearners: result.learners,
          // TODO: This is the first-page count only. The API does not return a
          // total cohort size; update once a totalCount field is available.
          audienceCount: result.learners.length,
        });
        return true;
      }
      case 'DISAMBIGUATION_NEEDED': {
        setStatus('disambiguation');
        setWarnings(result.warnings);
        setErrorMessage(_t('Please be more specific about which learners you want to reach.'));
        return false;
      }
      case 'ERROR': {
        setStatus('error');
        setErrorMessage(_t('Unable to define the cohort. Please try again.'));
        return false;
      }
      default:
        return false;
    }
  }, [orgId, programIds, mutateCreateCohort]);

  const cohortAudience: CohortAudience | undefined = React.useMemo(() => {
    if (!cohortId || !orgId) return undefined;
    return { kind: 'cohort', cohortId, orgId };
  }, [cohortId, orgId]);

  const dismissError = React.useCallback(() => {
    setStatus('idle');
    setErrorMessage(null);
  }, []);

  const resolveSendArgs = React.useCallback<
    (ctaText: string, callToAction?: string, audienceDelta?: AudienceDelta) => CohortSendArgs | null
  >(
    (ctaText: string, callToAction?: string, audienceDelta?: AudienceDelta): CohortSendArgs | null => {
      if (!cohortId) return null;
      const hasCta = ctaText.trim().length > 0;

      let resolvedCallToAction: string;
      if (!hasCta) {
        resolvedCallToAction = 'DO_NOT_INCLUDE';
      } else if (callToAction && callToAction !== 'DO_NOT_INCLUDE') {
        // Use the CTA the generation API actually classified for this prompt.
        resolvedCallToAction = callToAction;
      } else {
        // Fall back to FIND_A_COURSE when there's no classified CTA, or when
        // it's a stale DO_NOT_INCLUDE from a generation the admin has since
        // overridden by typing a label — an explicit label always gets a button.
        resolvedCallToAction = 'FIND_A_COURSE';
      }

      return {
        orgId,
        cohortId,
        preset: 'custom',
        callToAction: resolvedCallToAction,
        audienceDelta,
      };
    },
    [cohortId, orgId]
  );

  return {
    createCohort,
    status,
    cohortId,
    isCreating,
    errorMessage,
    warnings,
    cohortAudience,
    resolveSendArgs,
    dismissError,
  };
};

export default useAICohortFlow;
